import { useEffect, useRef, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Empty } from "@/components/ui-kit";
import { fmtTime } from "@/components/Pager";
import { toast } from "sonner";
import { Bus, Play, Square, AlertTriangle, Clock, Wrench, Phone, Navigation } from "lucide-react";
import { STATUS, card, LocationNote } from "./common";

function useGpsShare(trip) {
  const [state, setState] = useState({ last: null, error: "" });
  const lastSent = useRef(0);
  useEffect(() => {
    if (!trip) { return undefined; }
    if (!navigator.geolocation) { setState({ last: null, error: "This phone does not support GPS sharing." }); return undefined; }
    const id = navigator.geolocation.watchPosition((p) => {
      if (Date.now() - lastSent.current < 15000) return;
      lastSent.current = Date.now();
      api.post(`/transport/trips/${trip.id}/location`, { lat: p.coords.latitude, lng: p.coords.longitude, accuracy: p.coords.accuracy, speed: p.coords.speed })
        .then((r) => setState({ last: r.data, error: "" })).catch((e) => setState((s) => ({ ...s, error: formatApiError(e) })));
    }, (err) => setState((s) => ({ ...s, error: err.code === 1 ? "Location permission denied — parents will see the last known location only." : "GPS signal unavailable right now." })),
    { enableHighAccuracy: true, maximumAge: 10000, timeout: 30000 });
    return () => navigator.geolocation.clearWatch(id);
  }, [trip?.id]); // eslint-disable-line react-hooks/exhaustive-deps
  return state;
}

function IncidentBox({ trip, onDone }) {
  const [minutes, setMinutes] = useState("");
  const [note, setNote] = useState("");
  async function send(kind) {
    if (kind === "emergency" && !window.confirm("Report an EMERGENCY to parents and the principal?")) return;
    try {
      await api.post(`/transport/trips/${trip.id}/incident`, { kind, minutes: minutes ? Number(minutes) : null, note });
      toast.success("Reported and parents notified"); setMinutes(""); setNote(""); onDone();
    } catch (e) { toast.error(formatApiError(e)); }
  }
  return (
    <div data-testid="incident-box" className={`${card} space-y-3`}>
      <h3 className="font-display font-bold text-slate-900">Report a problem</h3>
      <div className="grid sm:grid-cols-3 gap-2">
        <Input data-testid="incident-minutes" type="number" min="1" placeholder="Delay (minutes)" value={minutes} onChange={(e) => setMinutes(e.target.value)} />
        <Input data-testid="incident-note" className="sm:col-span-2" placeholder="Short note (optional)" value={note} onChange={(e) => setNote(e.target.value)} />
      </div>
      <div className="flex flex-wrap gap-2">
        <Button data-testid="report-delay" variant="outline" onClick={() => send("delay")}><Clock size={15} className="mr-1" /> Delay</Button>
        <Button data-testid="report-breakdown" variant="outline" onClick={() => send("breakdown")}><Wrench size={15} className="mr-1" /> Breakdown</Button>
        <Button data-testid="report-emergency" className="bg-rose-600 hover:bg-rose-700 text-white" onClick={() => send("emergency")}><AlertTriangle size={15} className="mr-1" /> Emergency</Button>
      </div>
    </div>
  );
}

function StudentRow({ s, trip, onMark }) {
  const opts = trip.kind === "pickup" ? ["picked_up", "absent"] : ["dropped_off", "absent"];
  return (
    <div data-testid={`rider-${s.student_id}`} className="flex flex-wrap items-center gap-2 py-2 border-b border-slate-100 last:border-0">
      <div className="flex-1 min-w-[140px]">
        <div className="font-semibold text-slate-800 text-sm">{s.name}</div>
        <div className="text-xs text-slate-500">{s.class_name}-{s.section}{s.mark ? ` • ${STATUS[s.mark.status][0]} at ${fmtTime(s.mark.at)}` : ""}</div>
      </div>
      {s.parent_phone && <a href={`tel:${s.parent_phone}`} className="text-violet-700" data-testid={`rider-call-${s.student_id}`}><Phone size={16} /></a>}
      {opts.map((o) => (
        <button key={o} data-testid={`mark-${o}-${s.student_id}`} onClick={() => onMark(s, o)}
          className={`text-xs font-semibold px-3 py-1.5 rounded-full transition-colors ${s.mark?.status === o ? STATUS[o][1] + " ring-2 ring-offset-1 ring-violet-300" : "bg-slate-100 text-slate-600 hover:bg-violet-100"}`}>{STATUS[o][0]}</button>
      ))}
    </div>
  );
}

export function DriverView() {
  const [d, setD] = useState(null);
  const [err, setErr] = useState("");
  const load = () => api.get("/transport/my").then((r) => { setD(r.data); setErr(""); }).catch((e) => setErr(formatApiError(e)));
  useEffect(() => { load(); }, []);
  const trip = d?.active_trip;
  const gps = useGpsShare(trip);
  if (err) return <Empty testid="driver-empty" icon={Bus} text={err} />;
  if (!d) return <div className="h-40 rounded-2xl bg-white animate-pulse" />;
  async function start(kind) { try { await api.post("/transport/trips", { kind }); toast.success("Trip started — parents notified"); load(); } catch (e) { toast.error(formatApiError(e)); } }
  async function end() { if (!window.confirm("End this trip?")) return; try { await api.post(`/transport/trips/${trip.id}/end`); load(); } catch (e) { toast.error(formatApiError(e)); } }
  async function mark(s, status) { try { await api.post(`/transport/trips/${trip.id}/mark`, { student_id: s.student_id, status }); load(); } catch (e) { toast.error(formatApiError(e)); } }
  const stops = d.route?.stops || [];
  const riders = trip ? d.students.filter((s) => ["both", trip.kind].includes(s.mode)) : d.students;
  return (
    <div className="space-y-5">
      <div data-testid="driver-vehicle" className={`${card} grid sm:grid-cols-3 gap-4`}>
        <div><div className="text-xs text-slate-500">Vehicle</div><div className="font-display text-xl font-bold text-violet-800">{d.vehicle.reg_no}</div><div className="text-xs text-slate-500">{d.vehicle.model}</div></div>
        <div><div className="text-xs text-slate-500">Route</div><div className="font-semibold">{d.route?.name || "No route assigned"}</div><div className="text-xs text-slate-500">{stops.length} stops • {d.students.length} students</div></div>
        <div className="text-sm"><div>Driver: <b>{d.driver?.name || "—"}</b></div><div>Attendant: <b>{d.attendant?.name || "—"}</b></div></div>
      </div>
      {!trip ? (
        <div className={`${card} flex flex-wrap gap-3 items-center`}>
          <span className="text-sm text-slate-600 flex-1">No trip running. Start a trip to share live location and mark students.</span>
          <Button data-testid="start-pickup" className="btn-brand" onClick={() => start("pickup")} disabled={!d.route}><Play size={15} className="mr-1" /> Start pickup trip</Button>
          <Button data-testid="start-drop" variant="outline" onClick={() => start("drop")} disabled={!d.route}><Play size={15} className="mr-1" /> Start drop trip</Button>
        </div>
      ) : (
        <div data-testid="active-trip" className={`${card} space-y-2 border-l-4 border-emerald-500`}>
          <div className="flex flex-wrap items-center gap-2">
            <Badge className="bg-emerald-100 text-emerald-800">{trip.kind === "pickup" ? "Pickup trip" : "Drop trip"} running</Badge>
            <span className="text-xs text-slate-500">since {fmtTime(trip.started_at)}</span>
            <Button data-testid="end-trip" size="sm" variant="outline" className="ml-auto" onClick={end}><Square size={14} className="mr-1" /> End trip</Button>
          </div>
          <div className="flex items-center gap-2 text-sm"><Navigation size={15} className="text-violet-700" /> Sharing this phone's location while the trip runs.</div>
          {gps.error ? <div data-testid="gps-error" className="text-sm text-amber-700">{gps.error}</div> : <LocationNote loc={gps.last || trip.last_location} testid="gps-status" />}
        </div>
      )}
      {trip && <IncidentBox trip={trip} onDone={load} />}
      <div className={card}>
        <h3 className="font-display font-bold text-slate-900 mb-2">Students by stop</h3>
        {riders.length === 0 ? <div className="text-sm text-slate-500">No students assigned to this route.</div> : stops.map((st, i) => {
          const list = riders.filter((s) => s.stop_id === st.id);
          if (!list.length) return null;
          return (
            <div key={st.id} data-testid={`stop-${st.id}`} className="mb-3">
              <div className="text-xs font-bold text-violet-700 uppercase tracking-wide">{i + 1}. {st.name} {st.pickup_time ? `• pickup ${st.pickup_time}` : ""} {st.drop_time ? `• drop ${st.drop_time}` : ""}</div>
              {list.map((s) => trip ? <StudentRow key={s.student_id} s={s} trip={trip} onMark={mark} /> : <div key={s.student_id} className="text-sm py-1">{s.name} <span className="text-xs text-slate-400">{s.class_name}-{s.section}</span></div>)}
            </div>
          );
        })}
      </div>
      {(d.trips.length > 0 || d.incidents.length > 0) && (
        <div className={`${card} text-sm space-y-1`} data-testid="driver-today">
          <h3 className="font-display font-bold text-slate-900 mb-1">Today</h3>
          {d.trips.map((t) => <div key={t.id}>{t.kind === "pickup" ? "Pickup" : "Drop"} trip • {fmtTime(t.started_at)} – {t.ended_at ? fmtTime(t.ended_at) : "running"}</div>)}
          {d.incidents.map((x) => <div key={x.id} className="text-amber-700">{x.kind} • {fmtTime(x.at)} {x.note}</div>)}
        </div>
      )}
    </div>
  );
}
